import { useState, useEffect, useRef, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Settings, Brain, Users, BookOpen, Loader2, Trash2, Search, UserPlus, Crown, UsersRound, Upload, FileText, FileSpreadsheet, Presentation, Image } from 'lucide-react';
import { useProjectFiles, useProjectFileStats, useUploadProjectFile, useDeleteProjectFile } from '@/hooks/useProjectFiles';
import { ACCEPT_ALL, MAX_PROJECT_FILES, MAX_PROJECT_TOKENS, isSupportedExtension, formatFileSize, validateUploadSize } from '@/lib/fileConstants';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { useNavigation } from '@/contexts/NavigationContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/hooks/useProfile';
import { useUpdateProject, useDeleteProject } from '@/hooks/useProjects';
import { useTeams } from '@/hooks/useTeams';
import { useAllTeams } from '@/hooks/useAllTeams';
import {
  useProjectMembers,
  useAddProjectMember,
  useUpdateProjectMemberRole,
  useRemoveProjectMember,
} from '@/hooks/useProjectMembers';
import {
  useProjectTeams,
  useAddProjectTeam,
  useUpdateProjectTeamRole,
  useRemoveProjectTeam,
} from '@/hooks/useProjectTeams';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ContextBudgetIndicator } from '@/components/ContextBudgetIndicator';

const generalSchema = z.object({
  name: z.string().trim().min(2, 'Nome muito curto').max(100),
  icon: z.string().trim().min(1).max(10),
  description: z.string().trim().max(500).optional().or(z.literal('')),
});
type GeneralValues = z.infer<typeof generalSchema>;

const instructionsSchema = z.object({
  instructions: z.string().trim().max(500000).optional().or(z.literal('')),
});
type InstructionsValues = z.infer<typeof instructionsSchema>;

type Tab = 'geral' | 'instrucoes' | 'arquivos' | 'permissoes';

export const ProjectSettingsPage = () => {
  const { navigation, navigateToProject, setEditingResource } = useNavigation();
  const projectId = navigation.projectId!;
  const { user } = useAuth();
  const { isAdmin } = useProfile();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<Tab>('geral');
  const [deleteOpen, setDeleteOpen] = useState(false);

  const { data: project, isLoading } = useQuery({
    queryKey: ['project-full', projectId],
    enabled: !!projectId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('id', projectId)
        .single();
      if (error) throw error;
      return data;
    },
  });

  const isOwner = !!project && project.owner_id === user?.id;
  const canEdit = isOwner || isAdmin;

  const updateProject = useUpdateProject();
  const deleteProject = useDeleteProject();

  const generalForm = useForm<GeneralValues>({
    resolver: zodResolver(generalSchema),
    defaultValues: { name: '', icon: '📁', description: '' },
  });
  const instructionsForm = useForm<InstructionsValues>({
    resolver: zodResolver(instructionsSchema),
    defaultValues: { instructions: '' },
  });

  useEffect(() => {
    if (project) {
      generalForm.reset({
        name: project.name,
        icon: project.icon,
        description: project.description ?? '',
      });
      instructionsForm.reset({
        instructions: project.instructions ?? '',
      });
    }
  }, [project]);

  const onSaveGeneral = async (values: GeneralValues) => {
    try {
      await updateProject.mutateAsync({
        id: projectId,
        name: values.name,
        icon: values.icon,
        description: values.description || null,
      });
      queryClient.invalidateQueries({ queryKey: ['project-full', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      toast.success('Projeto atualizado');
    } catch (err) {
      toast.error(`Erro: ${(err as Error).message}`);
    }
  };

  const onSaveInstructions = async (values: InstructionsValues) => {
    try {
      await updateProject.mutateAsync({
        id: projectId,
        instructions: values.instructions || null,
      });
      queryClient.invalidateQueries({ queryKey: ['project-full', projectId] });
      toast.success('Instruções atualizadas');
    } catch (err) {
      toast.error(`Erro: ${(err as Error).message}`);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteProject.mutateAsync(projectId);
      toast.success('Projeto excluído');
      setEditingResource(null);
      // Voltar para o ambiente apropriado
      if (project?.scope_type === 'personal') {
        window.location.href = '/app';
      } else {
        window.location.href = '/app';
      }
    } catch (err) {
      toast.error(`Erro: ${(err as Error).message}`);
    }
  };

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-tertiary" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex-1 flex items-center justify-center text-tertiary text-sm">
        Projeto não encontrado.
      </div>
    );
  }

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'geral', label: 'Geral', icon: <Settings className="w-4 h-4" /> },
    { id: 'instrucoes', label: 'Instruções', icon: <Brain className="w-4 h-4" /> },
    { id: 'arquivos', label: 'Arquivos', icon: <BookOpen className="w-4 h-4" /> },
    { id: 'permissoes', label: 'Permissões', icon: <Users className="w-4 h-4" /> },
  ];

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-color flex items-center gap-3 shrink-0">
        <button
          onClick={() => { setEditingResource(null); navigateToProject(projectId, project.scope_type === 'personal'); }}
          className="p-1.5 hover:bg-hover rounded-md text-secondary"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <span className="text-2xl">{project.icon}</span>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-semibold text-primary truncate">{project.name}</h1>
          <p className="text-xs text-tertiary">Configurações do projeto</p>
        </div>
        {!canEdit && (
          <span className="text-xs text-tertiary px-2 py-1 bg-secondary rounded">Somente leitura</span>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-color px-6 flex gap-1 shrink-0">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={cn(
              'px-3 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-2',
              activeTab === t.id
                ? 'border-brand-primary text-primary'
                : 'border-transparent text-secondary hover:text-primary',
            )}
          >
            {t.icon}
            {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-2xl mx-auto">
          {activeTab === 'geral' && (
            <form onSubmit={generalForm.handleSubmit(onSaveGeneral)} className="space-y-4">
              <div className="flex gap-3">
                <div className="w-16">
                  <label className="block text-sm font-medium text-primary mb-1.5">Ícone</label>
                  <input
                    {...generalForm.register('icon')}
                    disabled={!canEdit}
                    className="w-full px-2 py-2 rounded-md border border-color bg-surface text-center text-lg focus:outline-none focus:ring-2 focus:ring-brand-primary disabled:opacity-60"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-primary mb-1.5">Nome *</label>
                  <input
                    {...generalForm.register('name')}
                    disabled={!canEdit}
                    className="w-full px-3 py-2 rounded-md border border-color bg-surface text-sm text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary disabled:opacity-60"
                  />
                  {generalForm.formState.errors.name && (
                    <p className="text-xs text-destructive mt-1">{generalForm.formState.errors.name.message}</p>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-primary mb-1.5">Descrição</label>
                <textarea
                  {...generalForm.register('description')}
                  disabled={!canEdit}
                  rows={3}
                  className="w-full px-3 py-2 rounded-md border border-color bg-surface text-sm text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary resize-none disabled:opacity-60"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-primary mb-1.5">Ambiente</label>
                <input
                  value={project.scope_type === 'global' ? '🏢 Espaço Caldeira' : '👤 Pessoal'}
                  disabled
                  className="w-full px-3 py-2 rounded-md border border-color bg-secondary text-sm text-tertiary"
                />
                <p className="text-xs text-tertiary mt-1">O ambiente do projeto não pode ser alterado.</p>
              </div>

              {canEdit && (
                <div className="flex justify-between pt-4 border-t border-color">
                  <button
                    type="button"
                    onClick={() => setDeleteOpen(true)}
                    className="px-4 py-2 text-sm font-medium text-destructive hover:bg-destructive/10 rounded-md flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    Excluir projeto
                  </button>
                  <button
                    type="submit"
                    disabled={updateProject.isPending}
                    className="px-4 py-2 brand-primary rounded-md text-sm font-medium disabled:opacity-50"
                  >
                    {updateProject.isPending ? 'Salvando...' : 'Salvar'}
                  </button>
                </div>
              )}
            </form>
          )}

          {activeTab === 'instrucoes' && (
            <form onSubmit={instructionsForm.handleSubmit(onSaveInstructions)} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-primary mb-1.5">
                  Instruções persistentes
                </label>
                <p className="text-xs text-tertiary mb-2">
                  Estas instruções serão injetadas no contexto de todas as conversas dentro deste projeto.
                </p>
                <textarea
                  {...instructionsForm.register('instructions')}
                  disabled={!canEdit}
                  rows={12}
                  className="w-full px-3 py-2 rounded-md border border-color bg-surface text-sm text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary resize-none disabled:opacity-60 font-mono"
                  placeholder="Você é um assistente especializado em..."
                />
                <ContextBudgetIndicator value={instructionsForm.watch('instructions') ?? ''} />
              </div>
              {canEdit && (
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={updateProject.isPending}
                    className="px-4 py-2 brand-primary rounded-md text-sm font-medium disabled:opacity-50"
                  >
                    {updateProject.isPending ? 'Salvando...' : 'Salvar instruções'}
                  </button>
                </div>
              )}
            </form>
          )}

          {activeTab === 'arquivos' && (
            <FilesPanel projectId={projectId} canEdit={canEdit} />
          )}

          {activeTab === 'permissoes' && (
            <PermissionsPanel projectId={projectId} ownerId={project.owner_id} canEdit={canEdit} />
          )}
        </div>
      </div>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir projeto?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação é permanente. Todas as conversas, arquivos, agentes e prompts deste projeto serão removidos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ============================================================
// FilesPanel: upload e gerenciamento de arquivos do projeto
// ============================================================

function getFileIcon(fileType: string) {
  switch (fileType) {
    case 'xlsx': case 'csv': return <FileSpreadsheet className="w-4 h-4 text-green-500 shrink-0" />;
    case 'pptx': return <Presentation className="w-4 h-4 text-orange-500 shrink-0" />;
    case 'image': return <Image className="w-4 h-4 text-purple-500 shrink-0" />;
    default: return <FileText className="w-4 h-4 text-blue-500 shrink-0" />;
  }
}

function progressColor(ratio: number): string {
  if (ratio >= 1) return 'bg-red-500';
  if (ratio >= 0.8) return 'bg-yellow-500';
  return 'bg-brand-primary';
}

const FilesPanel = ({ projectId, canEdit }: { projectId: string; canEdit: boolean }) => {
  const { data: files, isLoading } = useProjectFiles(projectId);
  const { data: stats } = useProjectFileStats(projectId);
  const uploadFile = useUploadProjectFile();
  const deleteFile = useDeleteProjectFile();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFileSelect = useCallback((file: File) => {
    if (!isSupportedExtension(file.name)) {
      toast.error(`Tipo de arquivo não suportado: .${file.name.split('.').pop()}`);
      return;
    }
    const sizeError = validateUploadSize(file);
    if (sizeError) {
      toast.error(sizeError);
      return;
    }
    if ((files?.length ?? 0) >= MAX_PROJECT_FILES) {
      toast.error(`Limite de ${MAX_PROJECT_FILES} arquivos por projeto atingido`);
      return;
    }
    uploadFile.mutate({ projectId, file }, {
      onSuccess: () => toast.success(`"${file.name}" enviado com sucesso`),
      onError: (err) => toast.error(`Erro ao enviar: ${err.message}`),
    });
  }, [projectId, files?.length, uploadFile]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    droppedFiles.forEach(f => handleFileSelect(f));
  }, [handleFileSelect]);

  const handleDeleteFile = useCallback((fileId: string, storagePath: string, fileName: string) => {
    deleteFile.mutate({ fileId, storagePath, projectId }, {
      onSuccess: () => toast.success(`"${fileName}" removido`),
      onError: (err) => toast.error(`Erro ao remover: ${err.message}`),
    });
  }, [projectId, deleteFile]);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-sm font-semibold text-primary mb-1">Base de conhecimento</h3>
        <p className="text-xs text-tertiary mb-4">
          Arquivos enviados aqui serão incluídos automaticamente como contexto em todas as conversas dentro deste projeto.
        </p>
      </div>

      {/* Usage bars */}
      <div className="space-y-3">
        <div>
          <div className="flex justify-between text-xs text-secondary mb-1">
            <span>Arquivos</span>
            <span>{stats?.file_count ?? 0} / {MAX_PROJECT_FILES}</span>
          </div>
          <div className="h-2 rounded-full bg-secondary overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${progressColor((stats?.file_count ?? 0) / MAX_PROJECT_FILES)}`}
              style={{ width: `${Math.min(100, ((stats?.file_count ?? 0) / MAX_PROJECT_FILES) * 100)}%` }}
            />
          </div>
        </div>
        <div>
          <div className="flex justify-between text-xs text-secondary mb-1">
            <span>Tokens</span>
            <span>{(stats?.total_tokens ?? 0).toLocaleString()} / {MAX_PROJECT_TOKENS.toLocaleString()}</span>
          </div>
          <div className="h-2 rounded-full bg-secondary overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${progressColor((stats?.total_tokens ?? 0) / MAX_PROJECT_TOKENS)}`}
              style={{ width: `${Math.min(100, ((stats?.total_tokens ?? 0) / MAX_PROJECT_TOKENS) * 100)}%` }}
            />
          </div>
        </div>
      </div>

      {/* Dropzone */}
      {canEdit && (
        <div
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`flex flex-col items-center justify-center gap-2 px-4 py-8 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
            isDragging ? 'border-brand-primary bg-hover' : 'border-color hover:bg-hover'
          }`}
        >
          <Upload className={`w-8 h-8 ${isDragging ? 'text-brand-primary' : 'text-secondary'}`} />
          <span className="text-sm text-secondary">Arraste arquivos ou clique para selecionar</span>
          <span className="text-xs text-tertiary">PDF, DOCX, PPTX, XLSX, TXT, MD, CSV, PNG, JPG, GIF, WebP — máx. 128 MB</span>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT_ALL}
            multiple
            className="hidden"
            onChange={(e) => {
              const selectedFiles = Array.from(e.target.files ?? []);
              selectedFiles.forEach(f => handleFileSelect(f));
              e.target.value = '';
            }}
          />
        </div>
      )}

      {/* File list */}
      <div>
        <label className="block text-sm font-medium text-primary mb-2">Arquivos ({files?.length ?? 0})</label>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-tertiary" />
          </div>
        ) : !files?.length ? (
          <p className="text-sm text-secondary text-center py-6">Nenhum arquivo na base de conhecimento</p>
        ) : (
          <div className="space-y-1.5">
            {files.map((file) => (
              <div key={file.id} className="group flex items-center gap-2 p-2.5 rounded-lg bg-secondary hover:bg-hover transition-colors">
                {getFileIcon(file.file_type)}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-primary truncate">{file.file_name}</p>
                  <p className="text-xs text-tertiary">{formatFileSize(file.file_size_bytes)}</p>
                </div>
                <TooltipProvider>
                  {file.parsing_status === 'pending' && (
                    <Badge variant="outline" className="text-xs bg-yellow-500/10 text-yellow-600 border-yellow-500/30 shrink-0">Pendente</Badge>
                  )}
                  {file.parsing_status === 'processing' && (
                    <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-600 border-blue-500/30 animate-pulse shrink-0">Processando</Badge>
                  )}
                  {file.parsing_status === 'done' && (
                    <Badge variant="outline" className="text-xs bg-green-500/10 text-green-600 border-green-500/30 shrink-0">
                      {file.token_count?.toLocaleString()} tokens
                    </Badge>
                  )}
                  {file.parsing_status === 'ready' && (
                    <Badge variant="outline" className="text-xs bg-green-500/10 text-green-600 border-green-500/30 shrink-0">Pronto</Badge>
                  )}
                  {file.parsing_status === 'failed' && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge variant="outline" className="text-xs bg-red-500/10 text-red-600 border-red-500/30 cursor-help shrink-0">Falhou</Badge>
                      </TooltipTrigger>
                      <TooltipContent side="left" className="max-w-[200px]">
                        <p className="text-xs">{file.parsing_error || 'Erro desconhecido'}</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                </TooltipProvider>
                {canEdit && (
                  <button
                    onClick={() => handleDeleteFile(file.id, file.storage_path, file.file_name)}
                    className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-active transition-all"
                    disabled={deleteFile.isPending}
                  >
                    <Trash2 className="w-3.5 h-3.5 text-red-500" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================
// PermissionsPanel: pessoas (project_members) + times (project_team_members)
// ============================================================

const PermissionsPanel = ({ projectId, ownerId, canEdit }: { projectId: string; ownerId: string; canEdit: boolean }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { data: members, isLoading: loadingMembers, isError: membersError, error: membersErrorData } = useProjectMembers(projectId);
  const { data: teams, isLoading: loadingTeams } = useProjectTeams(projectId);
  const { isAdmin } = useProfile();
  const { data: myTeams } = useTeams();
  const { data: allTeams } = useAllTeams();
  const teamsSource = isAdmin ? (allTeams ?? []).map(t => ({ id: t.id, name: t.name, icon: t.icon })) : (myTeams ?? []);

  const addMember = useAddProjectMember();
  const updateMemberRole = useUpdateProjectMemberRole();
  const removeMember = useRemoveProjectMember();
  const addTeam = useAddProjectTeam();
  const updateTeamRole = useUpdateProjectTeamRole();
  const removeTeam = useRemoveProjectTeam();

  const [searchQuery, setSearchQuery] = useState('');
  const [inviteRole, setInviteRole] = useState<'editor' | 'viewer'>('viewer');
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null);
  const [removingTeamId, setRemovingTeamId] = useState<string | null>(null);
  const [teamToAdd, setTeamToAdd] = useState<string>('');
  const [teamRoleToAdd, setTeamRoleToAdd] = useState<'editor' | 'viewer'>('viewer');

  const { data: searchResults } = useQuery({
    queryKey: ['profile-search-project', projectId, searchQuery],
    enabled: searchQuery.length >= 2 && canEdit,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, nome, email, avatar_url')
        .or(`email.ilike.%${searchQuery}%,nome.ilike.%${searchQuery}%`)
        .limit(10);
      if (error) throw error;
      const memberIds = new Set(members?.map(m => m.user_id) ?? []);
      memberIds.add(ownerId);
      return (data ?? []).filter(p => !memberIds.has(p.id));
    },
  });

  const handleInvite = (userId: string) => {
    addMember.mutate(
      { projectId, userId, role: inviteRole },
      {
        onSuccess: () => { setSearchQuery(''); toast.success('Pessoa adicionada'); },
          onError: (err: Error) => {
            queryClient.invalidateQueries({ queryKey: ['project-members', projectId] });
            const isDuplicate = err.message.toLowerCase().includes('duplicate key') || err.message.toLowerCase().includes('already exists');
            toast.error(isDuplicate ? 'Esta pessoa já tem acesso ao projeto' : `Erro: ${err.message}`);
          },
      },
    );
  };

  const handleAddTeam = () => {
    if (!teamToAdd) return;
    addTeam.mutate(
      { projectId, teamId: teamToAdd, role: teamRoleToAdd },
      {
        onSuccess: () => { setTeamToAdd(''); toast.success('Time adicionado'); },
        onError: (err: Error) => toast.error(`Erro: ${err.message}`),
      },
    );
  };

  const availableTeams = teamsSource.filter(
    t => !teams?.some(pt => pt.team_id === t.id),
  );

  return (
    <div className="space-y-8">
      {/* Owner */}
      <section>
        <h3 className="text-sm font-semibold text-primary mb-2 flex items-center gap-2">
          <Crown className="w-4 h-4 text-amber-500" />
          Dono do projeto
        </h3>
        <OwnerCard ownerId={ownerId} />
      </section>

      {/* Times */}
      <section>
        <h3 className="text-sm font-semibold text-primary mb-2 flex items-center gap-2">
          <UsersRound className="w-4 h-4" />
          Times com acesso
        </h3>
        <p className="text-xs text-tertiary mb-3">
          Todos os membros dos times listados abaixo têm acesso ao projeto. Permissões individuais sempre prevalecem.
        </p>

        {canEdit && availableTeams.length > 0 && (
          <div className="flex gap-2 mb-3">
            <select
              value={teamToAdd}
              onChange={e => setTeamToAdd(e.target.value)}
              className="flex-1 px-3 py-2 rounded-md border border-color bg-surface text-sm text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary"
            >
              <option value="">Selecionar time...</option>
              {availableTeams.map(t => (
                <option key={t.id} value={t.id}>{t.icon} {t.name}</option>
              ))}
            </select>
            <select
              value={teamRoleToAdd}
              onChange={e => setTeamRoleToAdd(e.target.value as 'editor' | 'viewer')}
              className="px-3 py-2 rounded-md border border-color bg-surface text-sm text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary"
            >
              <option value="viewer">Visualizador</option>
              <option value="editor">Editor</option>
            </select>
            <button
              onClick={handleAddTeam}
              disabled={!teamToAdd || addTeam.isPending}
              className="px-4 py-2 brand-primary rounded-md text-sm font-medium disabled:opacity-50"
            >
              Adicionar
            </button>
          </div>
        )}

        <div className="border border-color rounded-md divide-y divide-border">
          {loadingTeams ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-4 h-4 animate-spin text-tertiary" />
            </div>
          ) : teams && teams.length > 0 ? (
            teams.map(pt => (
              <div key={pt.id} className="flex items-center gap-3 px-3 py-2.5">
                <span className="text-lg">{pt.team.icon}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-primary truncate">{pt.team.name}</p>
                  {pt.team.description && (
                    <p className="text-xs text-tertiary truncate">{pt.team.description}</p>
                  )}
                </div>
                {canEdit ? (
                  <select
                    value={pt.role}
                    onChange={e => updateTeamRole.mutate(
                      { id: pt.id, role: e.target.value, projectId },
                      {
                        onSuccess: () => toast.success('Permissão atualizada'),
                        onError: (err: Error) => toast.error(`Erro: ${err.message}`),
                      },
                    )}
                    className="px-2 py-1 rounded border border-color bg-surface text-xs text-primary focus:outline-none focus:ring-1 focus:ring-brand-primary"
                  >
                    <option value="viewer">Visualizador</option>
                    <option value="editor">Editor</option>
                  </select>
                ) : (
                  <span className="text-xs text-tertiary px-2 py-1">
                    {pt.role === 'editor' ? 'Editor' : 'Visualizador'}
                  </span>
                )}
                {canEdit && (
                  <button
                    onClick={() => setRemovingTeamId(pt.id)}
                    className="p-1 hover:bg-hover rounded text-tertiary hover:text-destructive"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))
          ) : (
            <p className="text-sm text-tertiary text-center py-6">Nenhum time com acesso.</p>
          )}
        </div>
      </section>

      {/* Pessoas */}
      <section>
        <h3 className="text-sm font-semibold text-primary mb-2 flex items-center gap-2">
          <Users className="w-4 h-4" />
          Pessoas com acesso
        </h3>
        <p className="text-xs text-tertiary mb-3">
          Convide pessoas individualmente. Permissões aqui sobrescrevem o acesso herdado de times.
        </p>

        {canEdit && (
          <div className="space-y-2 mb-3">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-tertiary" />
                <input
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Buscar por email ou nome..."
                  className="w-full h-10 pl-10 pr-4 rounded-md border border-color bg-surface text-sm text-primary placeholder:text-tertiary focus:outline-none focus:ring-2 focus:ring-brand-primary"
                />
              </div>
              <select
                value={inviteRole}
                onChange={e => setInviteRole(e.target.value as 'editor' | 'viewer')}
                className="px-3 py-2 rounded-md border border-color bg-surface text-sm text-primary focus:outline-none focus:ring-1 focus:ring-brand-primary"
              >
                <option value="viewer">Visualizador</option>
                <option value="editor">Editor</option>
              </select>
            </div>

            {searchResults && searchResults.length > 0 && (
              <div className="border border-color rounded-md max-h-40 overflow-y-auto">
                {searchResults.map(p => (
                  <button
                    key={p.id}
                    onClick={() => handleInvite(p.id)}
                    disabled={addMember.isPending}
                    className="w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-hover transition-colors"
                  >
                    <div className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center text-xs font-medium text-primary flex-shrink-0">
                      {p.avatar_url ? (
                        <img src={p.avatar_url} className="w-7 h-7 rounded-full object-cover" alt="" />
                      ) : (
                        (p.nome?.[0] ?? p.email[0]).toUpperCase()
                      )}
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <p className="text-primary truncate">{p.nome ?? 'Sem nome'}</p>
                      <p className="text-xs text-tertiary truncate">{p.email}</p>
                    </div>
                    <UserPlus className="w-4 h-4 text-tertiary flex-shrink-0" />
                  </button>
                ))}
              </div>
            )}
            {searchQuery.length >= 2 && searchResults?.length === 0 && (
              <p className="text-xs text-tertiary px-1">Nenhum usuário encontrado.</p>
            )}
          </div>
        )}

        <div className="border border-color rounded-md divide-y divide-border">
          {loadingMembers ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-4 h-4 animate-spin text-tertiary" />
            </div>
          ) : membersError ? (
            <div className="px-3 py-6 text-center">
              <p className="text-sm text-primary">Não foi possível carregar as pessoas convidadas.</p>
              <p className="text-xs text-tertiary mt-1">{membersErrorData?.message ?? 'Tente recarregar a página.'}</p>
            </div>
          ) : members && members.length > 0 ? (
            members.map(m => {
              const isSelf = m.user_id === user?.id;
              const displayName = m.profile?.nome ?? 'Perfil não encontrado';
              const displayEmail = m.profile?.email ?? 'Usuário sem perfil vinculado';
              const avatarInitial = (m.profile?.nome?.[0] ?? m.profile?.email?.[0] ?? '?').toUpperCase();
              return (
                <div key={m.id} className="flex items-center gap-3 px-3 py-2.5">
                  <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-xs font-medium text-primary flex-shrink-0">
                    {m.profile?.avatar_url ? (
                      <img src={m.profile.avatar_url} className="w-8 h-8 rounded-full object-cover" alt="" />
                    ) : (
                      avatarInitial
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-primary truncate">
                      {displayName}
                      {isSelf && <span className="text-xs text-tertiary ml-1">(você)</span>}
                    </p>
                    <p className="text-xs text-tertiary truncate">{displayEmail}</p>
                  </div>
                  {canEdit ? (
                    <select
                      value={m.role}
                      onChange={e => updateMemberRole.mutate(
                        { memberId: m.id, role: e.target.value, projectId },
                        {
                          onSuccess: () => toast.success('Permissão atualizada'),
                          onError: (err: Error) => toast.error(`Erro: ${err.message}`),
                        },
                      )}
                      className="px-2 py-1 rounded border border-color bg-surface text-xs text-primary focus:outline-none focus:ring-1 focus:ring-brand-primary"
                    >
                      <option value="viewer">Visualizador</option>
                      <option value="editor">Editor</option>
                    </select>
                  ) : (
                    <span className="text-xs text-tertiary px-2 py-1">
                      {m.role === 'editor' ? 'Editor' : 'Visualizador'}
                    </span>
                  )}
                  {canEdit && (
                    <button
                      onClick={() => setRemovingMemberId(m.id)}
                      className="p-1 hover:bg-hover rounded text-tertiary hover:text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              );
            })
          ) : (
            <p className="text-sm text-tertiary text-center py-6">Nenhuma pessoa convidada.</p>
          )}
        </div>
      </section>

      <AlertDialog open={!!removingMemberId} onOpenChange={() => setRemovingMemberId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover pessoa</AlertDialogTitle>
            <AlertDialogDescription>
              A pessoa perderá o acesso individual. Pode continuar acessando se pertencer a um time com acesso.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!removingMemberId) return;
                removeMember.mutate(
                  { memberId: removingMemberId, projectId },
                  {
                    onSuccess: () => { setRemovingMemberId(null); toast.success('Removido'); },
                    onError: (err: Error) => { setRemovingMemberId(null); toast.error(`Erro: ${err.message}`); },
                  },
                );
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >Remover</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!removingTeamId} onOpenChange={() => setRemovingTeamId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover time</AlertDialogTitle>
            <AlertDialogDescription>
              Os membros deste time perderão acesso ao projeto, exceto se foram convidados individualmente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!removingTeamId) return;
                removeTeam.mutate(
                  { id: removingTeamId, projectId },
                  {
                    onSuccess: () => { setRemovingTeamId(null); toast.success('Time removido'); },
                    onError: (err: Error) => { setRemovingTeamId(null); toast.error(`Erro: ${err.message}`); },
                  },
                );
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >Remover</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const OwnerCard = ({ ownerId }: { ownerId: string }) => {
  const { data: profile } = useQuery({
    queryKey: ['profile', ownerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, nome, email, avatar_url')
        .eq('id', ownerId)
        .single();
      if (error) throw error;
      return data;
    },
  });

  return (
    <div className="border border-color rounded-md px-3 py-2.5 flex items-center gap-3">
      <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-xs font-medium text-primary flex-shrink-0">
        {profile?.avatar_url ? (
          <img src={profile.avatar_url} className="w-8 h-8 rounded-full object-cover" alt="" />
        ) : (
          (profile?.nome?.[0] ?? profile?.email?.[0] ?? '?').toUpperCase()
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-primary truncate">{profile?.nome ?? 'Carregando...'}</p>
        <p className="text-xs text-tertiary truncate">{profile?.email}</p>
      </div>
      <span className="text-xs text-tertiary px-2 py-1 bg-secondary rounded">Owner</span>
    </div>
  );
};
